import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ApiConflictError,
  ApiError,
  ApiNetworkError,
  ApiServerError,
  isDuplicateWriteConflict,
} from '@/lib/api-client';

/**
 * Global TanStack Query client.
 *
 * Defaults:
 * - ``staleTime: 5 min`` — most agent data doesn't change once a run
 *   completes.
 * - ``retry: 1`` — single retry on transient failures; per-query
 *   override available for endpoints that should never retry (auth,
 *   dispatch) or that need their own retry schedule.
 * - ``refetchOnWindowFocus: false`` — clinical/regulatory pages do
 *   their own polling; refocus refetch would cause spurious re-runs of
 *   completed agent results.
 *
 * Global error handling (``QueryCache`` + ``MutationCache``):
 *   - 401 → already handled inline by the api-client (auto-redirect to
 *     /login). Cache hooks ignore it.
 *   - 409 (``ApiConflictError``) → typed application signal for
 *     amendment concurrency; the calling component branches on it.
 *     Cache hook stays quiet so the "Refresh and retry" copy lives
 *     next to the form, not in a toast. EXCEPT the backend's app-wide
 *     duplicate-write 409 (a double-click / two tabs hit a unique rule):
 *     no screen handles that one, and it used to be a 500 that showed
 *     the toast below — so it gets its own message here.
 *   - 5xx (``ApiServerError``) → global toast "Server is having
 *     trouble". Routes can't meaningfully recover; the global toast
 *     is the right surface.
 *   - Network failure (``ApiNetworkError``) → global toast "Can't
 *     reach the server".
 *   - Generic 4xx (``ApiError``) → SILENT at the global level. Route /
 *     mutation callsites already do their own ``toast.error(err.message)``
 *     in catch blocks (which include the server's actual ``detail``);
 *     toasting from here too would double-fire on every dispatch failure.
 *
 * Per spec §4.8: NO ``persistQueryClient``. Cache stays in-memory only;
 * regulatory + clinical data must not be persisted to browser disk.
 */
export function reportError(err: unknown) {
  // 401 is handled by the api-client interceptor (redirect to /login).
  if (err instanceof ApiError && err.status === 401) return;
  if (isDuplicateWriteConflict(err)) {
    toast.error((err as ApiError).message);
    return;
  }
  // Any other 409 is a typed application signal — the caller decides UX.
  if (err instanceof ApiConflictError) return;

  // Infrastructure failures only — route handlers can't add value here,
  // and the user needs to know the problem isn't on their end.
  if (err instanceof ApiNetworkError) {
    toast.error("Can't reach the server", {
      description: 'Check your connection and try again in a moment.',
    });
    return;
  }
  if (err instanceof ApiServerError) {
    toast.error('Something went wrong on our end', {
      description: 'Please try again in a moment.',
    });
    return;
  }
  // Generic 4xx + plain Errors stay silent — the route's own catch
  // block owns the user-facing message (see dispatch handlers across
  // strategy / protocol / report / dossier / predicate).
}

/**
 * Retry only TRANSIENT failures. A 4xx is deterministic — retrying a 404 / 403 /
 * 400 / 409 can't change the outcome; it just holds the caller on a "loading"
 * state through a pointless second round-trip. (Concretely: opening a vault link
 * you can't access should 404 immediately, not sit on "Document — Loading…" as if
 * it were being processed.) 5xx / network / unknown failures get a single retry,
 * matching the previously-documented "single retry on transient failures" intent.
 */
export function retryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 1;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: retryQuery,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: reportError,
  }),
  mutationCache: new MutationCache({
    onError: reportError,
  }),
});
